import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { ApplicationListGridComponent } from '../../application-list-grid/application-list-grid.component';
import { CONSTANTS } from 'src/Core/Constant';
import { GridPermissionByRole } from 'src/Core/Models/grid-permission-by-role.model';
import { TranslationService } from 'src/app/Service/translation.service';

@Component({
  selector: 'app-center-mapping',
  templateUrl: './center-mapping.component.html',
  styleUrls: ['./center-mapping.component.scss']
})
export class CenterMappingComponent implements OnInit, AfterViewInit {
  
  // Reference to the grid component
  @ViewChild('gridComponent') gridComponent!: ApplicationListGridComponent;

  // Flag to track if grid component is ready
  private isGridComponentReady = false;
  
  columnDefs: ColDef[] = [
    { headerName: 'Center Name', field: 'CenterName', sortable: true, filter: false },
    { headerName: 'State Code', field: 'IndianStateName', sortable: true, filter: false },
    { headerName: 'APP Name', field: 'APPName', sortable: true, filter: false },
    { headerName: 'AOC Name', field: 'AOCName', sortable: true, filter: false },
    { headerName: 'COC Name', field: 'COCName', sortable: true, filter: false },
  ];
  
  CONSTANTS = CONSTANTS;

  gridPermissionByRole: GridPermissionByRole = {
    isEditPermission: true,
    isGaoSendBackToAPP: false,
    gaoSendBackToAPPUrl: "GAO/GAOSendBackToApplicant",
    isApprovalPermission: false,
    isRejectPermission: false,
    isSendToFirstAidPermission: false,
    isAddNotesAddPermission: false,
    isFirstAidResolvePermission: false,
    isSendBackToApplicantPermission: false,
    isForwardToGAOPermission:false,
    getApplicationListUrlEndPoint:"Center/GetCentersODataAsync", // Updated to use OData endpoint
    sendBackToApplicantUrl: "APP/sendbackToApplicant",
    forwardToGAOP: "APP/ForwardToGAO",
    previewRoutePath: 'gao/preview',
    noteCreatorId: CONSTANTS.NoteCreatorId.SEVA_CENTER_NOTES
    
  }



  // Control action column visibility from parent
  showActionColumn: boolean = true;
  
  // Grid data from API response
  gridData: any[] = [];

  centerMappingForm: FormGroup;
  submitted = false;

  // Collapsible functionality
  isFilterCollapsed = false;

  toggleFilter() {
    this.isFilterCollapsed = !this.isFilterCollapsed;
  }


 

  // State and District lists (legacy – no longer used in UI)
  stateList: any = [];
  districtList: any = [];
  
  // Search timeouts (legacy)
  private searchTimeout: any;
  private districtSearchTimeout: any;
  
  // Search terms (legacy)
  stateSearchTerm: string = '';
  districtSearchTerm: string = '';

  // Center search term for textbox
  private centerSearchTimeout: any;

  constructor( 
    private headerService: GenericValidationsService, 
    private fb: FormBuilder, 
    private api: SevaProfileService,
    public translationService: TranslationService,
    private cdr: ChangeDetectorRef
  ) {
    // Center search textbox - no validation required since it's a search field
    this.centerMappingForm = this.fb.group({
      CenterSearchTerm: ['']
    });
  }



  ngOnInit(): void {
    this.headerService.setHeader('Applications', 'Center Mapping');
    this.initializeForm();
    // Initialize with empty grid data to prevent auto-fetch
    this.gridData = [];
    // Trigger change detection to ensure translations are rendered
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    // Grid component is now available
    this.isGridComponentReady = true;
    // Ensure grid stays blank - gridData is already initialized as empty array
  }

  initializeForm() {
    // No extra initialization needed for single centre dropdown
  }

  // Set up state change listener for district loading
  setupStateChangeListener() {
    const stateControl = this.centerMappingForm.get('StateId');
    if (stateControl) {
      stateControl.valueChanges.subscribe((stateId: string) => {
     
        if (stateId) {
          // Load districts for selected state
          this.getDistrictList(stateId);
          // Clear district selection
          this.centerMappingForm.patchValue({
            DistrictId: null
          });
        } else {
          // Clear district list when no state is selected
          this.districtList = [];
        }
      });
    }
  }

  // State API call - same as study-participation-information
  getStateList(searchTerm: string = '') {

    let reqBody = {
      "start": 0,
      "size": 5,
      "search": searchTerm,
      "sortfield": "StateName",
      "sortdir": "Asc"
    }

    this.api.apiCall('Center/GetAllStatesPaginatedResult', 'POST', reqBody).subscribe((response: any) => {
      this.stateList = response.Data.DataList;
    });

  }

  // State search functionality - same as study-participation-information
  onStateSearchInput(event: any) {
    const searchTerm = event.term || event;
    this.stateSearchTerm = searchTerm;

    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  
    if (searchTerm && searchTerm.length >= 3) {
 
      this.searchTimeout = setTimeout(() => {
        this.getStateList(searchTerm);
      }, 300);
    } else if (!searchTerm || searchTerm.length === 0) {
   
      this.stateSearchTerm = '';
      this.getStateList();
    }
  }

  // State clear functionality - same as study-participation-information
  onStateClear() {

    this.stateSearchTerm = '';
    this.getStateList();
    // Clear district when state is cleared
    this.districtList = [];
    this.centerMappingForm.patchValue({
      DistrictId: null
    });
  }

  // District API call - same as study-participation-information
  getDistrictList(stateId: string, searchTerm: string = '') {
    if (!stateId) {
      this.districtList = [];
      return;
    }
    
    // Find the selected state object to get the StateCode
    const selectedState = this.stateList.find((state: any) => state.Id == stateId);
    if (!selectedState) {
      this.districtList = [];
      return;
    }
    
    const stateCode = selectedState.StateCode;
    
    let reqBody = {
      "start": 0,
      "size": 5,
      "search": searchTerm,
      "sortfield": "DistrictName",
      "sortdir": "Asc",
    }


    this.api.apiCall(`Center/GetAllDistrictByStatePaginatedResult?stateId=${stateId}`, 'POST', reqBody).subscribe((response: any) => {
      this.districtList = response.Data.DataList;
 
    });
  }

  // District search functionality - same as study-participation-information
  onDistrictSearchInput(event: any) {
    const searchTerm = event.term || event;
    this.districtSearchTerm = searchTerm;

    
    if (this.districtSearchTimeout) {
      clearTimeout(this.districtSearchTimeout);
    }
  
    if (searchTerm && searchTerm.length >= 3) {

      this.districtSearchTimeout = setTimeout(() => {
        const stateId = this.centerMappingForm.get('StateId')?.value;
        this.getDistrictList(stateId, searchTerm);
      }, 300);
    } else if (!searchTerm || searchTerm.length === 0) {
 
      this.districtSearchTerm = '';
      const stateId = this.centerMappingForm.get('StateId')?.value;
      this.getDistrictList(stateId);
    }
  }

  // District clear functionality - same as study-participation-information
  onDistrictClear() {

    this.districtSearchTerm = '';
    const stateId = this.centerMappingForm.get('StateId')?.value;
    this.getDistrictList(stateId);
  }


  // Get form controls for validation
  get f() {
    return this.centerMappingForm.controls;
  }




  // Search functionality - search using OData with textbox value
  onSearch(): void {
    console.log('Search button clicked');
    const searchTerm = this.centerMappingForm.get('CenterSearchTerm')?.value?.trim() || '';
    
    if (searchTerm && searchTerm.length >= 3) {
      // Search using OData with center name filter
      this.searchCentersOData(searchTerm);
    } else if (searchTerm.length > 0 && searchTerm.length < 3) {
      console.log('Search term must be at least 3 characters');
      // Optionally show a toast message
    } else {
      // Clear grid if search term is empty
      this.gridData = [];
    }
  }

  // Search centers using GetCentersODataAsync
  searchCentersOData(searchTerm: string): void {
    if (!searchTerm || searchTerm.trim().length === 0) {
      this.gridData = [];
      return;
    }

    // Build OData query with $filter for CenterName
    // OData filter syntax: $filter=contains(CenterName,'searchTerm')
    // Escape single quotes in search term for OData
    const escapedSearchTerm = searchTerm.trim().replace(/'/g, "''");
    const filterParam = `$filter=contains(CenterName,'${escapedSearchTerm}')`;
    const orderByParam = `$orderby=CenterName`;
    const apiUrl = `Center/GetCentersODataAsync?${filterParam}&${orderByParam}`;

    console.log('Searching centers with OData:', apiUrl);

    this.api.apiCall(apiUrl, 'GET').subscribe(
      (response: any) => {
        console.log('OData API Response:', response);
        if (response && response.Data && response.Data.ItemsData) {
          // Map response data to grid format
          this.gridData = response.Data.ItemsData.map((item: any) => ({
            ...item,
            // Ensure StateCode field is mapped from IndianStateName for grid compatibility
            StateCode: item.IndianStateName || item.StateCode
          }));
          console.log('Grid data updated:', this.gridData.length, 'items');
        } else {
          this.gridData = [];
          console.log('No data in OData response');
        }
      },
      (error) => {
        console.error('OData API Error:', error);
        this.gridData = [];
      }
    );
  }


  // District API call (legacy, no longer used – kept for compatibility)
  getCenterList(DistrictId: any, searchTerm: string = '') {
    // Build query parameters for GET request
    const params = new URLSearchParams();
    params.append('DistrictId', DistrictId);
    params.append('start', '0');
    params.append('size', '5');
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    params.append('sortfield', 'CenterName');
    params.append('sortdir', 'Asc');
    
    const url = `Center/GetAllCentersByDistrictPaginatedResult?${params.toString()}`;
    
    this.api.apiCall(url, 'GET').subscribe((response: any) => {
      if (response.Data && response.Data.Items) {
        this.gridData = response.Data.Items;
      } else {
        this.gridData = [];
      }
    }, (error) => {
      console.error('API Error:', error);
      this.gridData = [];
    });
  }

  // Handle search input for center search textbox
  onCenterSearchInput(event: any): void {
    const searchTerm = event.target?.value || '';
    
    if (this.centerSearchTimeout) {
      clearTimeout(this.centerSearchTimeout);
    }

    if (searchTerm && searchTerm.trim().length >= 3) {
      // Debounce: wait 500ms after user stops typing before calling API
      this.centerSearchTimeout = setTimeout(() => {
        this.searchCentersOData(searchTerm.trim());
      }, 500);
    } else if (!searchTerm || searchTerm.trim().length === 0) {
      // Clear grid when search is cleared
      this.gridData = [];
    }
  }

  // Translation helper
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }
  // Clear functionality
  onClear(): void {
    console.log('Clear button clicked');
    this.submitted = false;
    this.centerMappingForm.reset();
    this.districtList = [];
    
    // Clear any pending search timeout
    if (this.centerSearchTimeout) {
      clearTimeout(this.centerSearchTimeout);
      this.centerSearchTimeout = null;
    }
    
    // Clear grid data - start with blank grid
    this.gridData = [];
    
    // Clear URL parameters if grid component is available
    if (this.isGridComponentReady && this.gridComponent) {
      this.gridComponent.clearAllUrlParameters();
    }
  }



}
